"use client";

import { useQuery } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { FlowContextProvider } from "@portal/app/_components/flow/FlowContext";
import { SignInFlowDocument } from "./generated";
import { BasicInfoStep } from "@portal/app/_components/flow/steps/BasicInfoStep";
import { PhoneVerificationStep } from "@portal/app/_components/flow/steps/PhoneVerificationStep";
import { SignedInStep } from "@portal/app/_components/flow/steps/SignedInStep";
import { SignInStep } from "@portal/app/_components/flow/steps/SignInStep";
import { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { SignInContainer } from "@portal/app/login/_components/SignInContainer";
import { TypographySkeleton } from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import { COOKIE_KEYS, getCookie } from "@portal/app/_common/lib/headers";

void gql`
  fragment SignInFlowFragment on SignInFlowState {
    id
    initialStep {
      id
      name
      status
    }
    steps {
      id
      name
      status
      isBlocking
    }
    hasBlockingStep
  }
  query signInFlow {
    signInFlow {
      ...SignInFlowFragment
    }
  }
`;

export type SignInType = "CREATE_ACCOUNT" | "SIGN_IN";
export const SignInFlowController = forwardRef(
  (
    {
      createAccountOrSignIn = "CREATE_ACCOUNT",
      showClose = true,
      onLoginSuccess,
    }: {
      createAccountOrSignIn: SignInType;
      showClose?: boolean;
      onLoginSuccess?: () => void;
    },
    ref
  ) => {
    const { data } = useQuery(SignInFlowDocument);
    const fbPixelBrowserId = getCookie(COOKIE_KEYS.FB_PIXEL_BROWSER_ID);
    const fbPixelClickId = getCookie(COOKIE_KEYS.FB_PIXEL_CLICK_ID);

    return (
      <SignInContainer
        ref={ref}
        showClose={showClose}
        onClose={() => {
          onLoginSuccess?.();
        }}
      >
        {!data ? (
          <>
            <TypographySkeleton sx={{ height: "6.9rem", mb: 2 }} />
            <TypographySkeleton sx={{ height: "6.6rem", mb: 2 }} />
            <TypographySkeleton />
          </>
        ) : (
          <FlowContextProvider<"signInFlow"> flow="signInFlow" flowState={data.signInFlow}>
            {({ stepShown }) => (
              <>
                {stepShown === "SIGN_IN_STEP" && <SignInStep signInType={createAccountOrSignIn} />}
                {stepShown === "BASIC_INFO_STEP" && (
                  <StepContainer>
                    <StepTitle stepName={stepShown}>Welcome to Dreambound</StepTitle>
                    <BasicInfoStep
                      data-testid="create-account"
                      mixpanelEventMap={{
                        nameLast: "Sign In - Create Account - Name Last",
                        nameFirst: "Sign In - Create Account - Name First",
                        email: "Sign In - Create Account - Email",
                        phone: "Sign In - Create Account - Phone",
                        submitted: "Sign In - Create Account",
                      }}
                      additionalCustomerDetails={{
                        accountCreationFunnel: "SIGN_IN",
                        ...(fbPixelBrowserId && { fbPixelBrowserId }),
                        ...(fbPixelClickId && { fbPixelClickId }),
                      }}
                    />
                  </StepContainer>
                )}
                {stepShown === "PHONE_VERIFICATION_STEP" && (
                  <StepContainer>
                    <StepTitle stepName={stepShown}>Welcome to Dreambound</StepTitle>
                    <PhoneVerificationStep
                      mixpanelEventMap={{
                        codeSent: "Sign In - Phone Verification - Code Sent",
                        codeVerified: "Sign In - Phone Verification - Code Verified",
                      }}
                    />
                  </StepContainer>
                )}
                {stepShown === "SIGNED_IN_STEP" && (
                  <SignedInStep mixpanelEvent="Sign In - Success" onLoginSuccess={onLoginSuccess} />
                )}
              </>
            )}
          </FlowContextProvider>
        )}
      </SignInContainer>
    );
  }
);

const StepContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  background: white;
`;

const StepTitle = ({
  stepName,
  ...props
}: TypographyProps & {
  stepName: string;
}) => (
  <Typography
    variant="page_title"
    sx={{ my: 2 }}
    {...props}
    data-testid={`${kebabCase(stepName)}-header`}
  />
);
